'use client'

import { useMemo, useState } from 'react'
import Image from 'next/image'
import { PiMagnifyingGlassLight } from 'react-icons/pi'
import { ChevronDown, ChevronUp, X } from 'lucide-react'
import type { CatalogItem } from '@/lib/businessOnboarding'

type CatalogPickerProps = {
  title?: string
  subtitle?: string
  searchPlaceholder: string
  actionLabel?: string
  onAction?: () => void
  sidebarTitle: string
  sidebarHint: string
  items: CatalogItem[]
  selected: CatalogItem[]
  loading?: boolean
  onSearch: (query: string) => void
  onToggle: (item: CatalogItem) => void
  onClear: () => void
  resultLabel?: string
  /** Use when embedding in a narrow container (e.g. a modal) instead of a full-width page —
   * always shows the selected-items list stacked above the grid instead of a side-by-side
   * `xl:` split, since that split is based on viewport width, not the container's actual width. */
  compact?: boolean
}

export default function CatalogPicker({
  title,
  subtitle,
  searchPlaceholder,
  actionLabel,
  onAction,
  sidebarTitle,
  sidebarHint,
  items,
  selected,
  loading,
  onSearch,
  onToggle,
  onClear,
  resultLabel = 'results found',
  compact = false,
}: CatalogPickerProps) {
  const [query, setQuery] = useState('')
  const [mobileOpen, setMobileOpen] = useState(false)

  const selectedIds = useMemo(() => new Set(selected.map((s) => s.id)), [selected])

  const handleSearch = (value: string) => {
    setQuery(value)
    onSearch(value)
  }

  return (
    <div className="mx-auto flex h-full w-full max-w-[1200px] flex-col px-4 sm:px-8">
      {title && (
        <div className="mb-6 text-center">
          <h1 className="font-inter text-[28px] font-semibold leading-9 text-[#202020] sm:text-[32px]">
            {title}
          </h1>
          {subtitle && (
            <p className="mx-auto mt-2 max-w-2xl font-inter text-base text-[#6B7280]">{subtitle}</p>
          )}
        </div>
      )}

      <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <PiMagnifyingGlassLight className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
          <input
            type="search"
            value={query}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder={searchPlaceholder}
            className="h-12 w-full rounded-lg border border-[#CBD7DD] pl-10 pr-4 text-base focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>
        {actionLabel && onAction && (
          <button
            type="button"
            onClick={onAction}
            className="h-12 whitespace-nowrap rounded-full bg-primary px-5 text-sm font-medium text-white hover:bg-primary/90"
          >
            {actionLabel}
          </button>
        )}
      </div>

      <div className="mb-3 flex items-center justify-between text-sm text-[#6B7280]">
        <span>
          {loading ? 'Loading…' : `${items.length} ${resultLabel}`}
        </span>
        {query.trim() && <span>Search: {query}</span>}
      </div>

      {compact ? (
        <div className="mb-3 flex flex-col gap-2 rounded-lg bg-[#F7F7F7] p-3">
          <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
            <span className="whitespace-nowrap font-semibold">
              {sidebarTitle} ({selected.length})
            </span>
            {selected.length > 0 && (
              <button type="button" onClick={onClear} className="whitespace-nowrap text-sm font-medium text-red-500">
                Clear all
              </button>
            )}
          </div>
          {selected.length === 0 ? (
            <p className="text-sm text-gray-500">{sidebarHint}</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {selected.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center gap-1.5 rounded-full border border-primary/30 bg-white py-1 pl-1 pr-2"
                >
                  <Thumb src={item.imageUrl} alt={item.name} />
                  <span className="text-sm font-medium">{item.name}</span>
                  <button type="button" onClick={() => onToggle(item)} aria-label={`Remove ${item.name}`}>
                    <X size={14} className="text-gray-400" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="mb-3 rounded-lg bg-[#F7F7F7] p-3 md:hidden">
          <button
            type="button"
            className="flex w-full items-center justify-between"
            onClick={() => setMobileOpen((v) => !v)}
          >
            <span className="font-semibold">
              {sidebarTitle} ({selected.length})
            </span>
            {mobileOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </button>
          {mobileOpen && (
            <div className="mt-3 space-y-2">
              {selected.length === 0 ? (
                <p className="text-sm text-gray-500">{sidebarHint}</p>
              ) : (
                selected.map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center gap-2 rounded-lg border border-primary/30 bg-white px-2 py-2"
                  >
                    <Thumb src={item.imageUrl} alt={item.name} />
                    <span className="flex-1 truncate text-sm font-medium">{item.name}</span>
                    <button type="button" onClick={() => onToggle(item)} aria-label={`Remove ${item.name}`}>
                      <X size={16} className="text-gray-400" />
                    </button>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      )}

      <div className={compact ? 'flex-1' : 'grid min-h-0 flex-1 gap-4 xl:grid-cols-12'}>
        <div className={compact ? '' : 'min-h-0 overflow-y-auto xl:col-span-8'}>
          {loading ? (
            <div className="flex h-40 items-center justify-center text-sm text-gray-500">Loading…</div>
          ) : items.length === 0 ? (
            <div className="flex h-40 items-center justify-center text-sm text-gray-500">
              No results found
            </div>
          ) : (
            <div className={compact ? 'grid grid-cols-2 gap-3' : 'grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3'}>
              {items.map((item) => {
                const isSelected = selectedIds.has(item.id)
                return (
                  <div
                    key={item.id}
                    className={`flex min-h-[220px] flex-col items-center justify-between rounded-xl border p-4 ${
                      isSelected
                        ? 'border-primary/40 bg-[#F3FAEB]'
                        : 'border-[#E6E6E6] bg-white'
                    }`}
                  >
                    <Thumb src={item.imageUrl} alt={item.name} large />
                    <div className="mt-3 text-center">
                      <p className="font-semibold text-[#202020]">{item.name}</p>
                      {item.subtitle && (
                        <p className="mt-1 text-xs text-gray-500">{item.subtitle}</p>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => onToggle(item)}
                      className={`mt-4 w-full rounded-full px-3 py-2 text-sm font-medium ${
                        isSelected
                          ? 'bg-[#E8F0E0] text-[#548828]'
                          : 'border border-primary text-primary hover:bg-primary/5'
                      }`}
                    >
                      {isSelected ? 'Added' : 'Add to List'}
                    </button>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {!compact && (
          <aside className="hidden rounded-xl bg-[#F7F7F7] p-4 xl:col-span-4 xl:block">
            <div className="mb-2 flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
              <h3 className="whitespace-nowrap font-semibold">
                {sidebarTitle} ({selected.length})
              </h3>
              {selected.length > 0 && (
                <button type="button" onClick={onClear} className="whitespace-nowrap text-sm font-medium text-red-500">
                  Clear all
                </button>
              )}
            </div>
            <p className="mb-4 text-sm text-gray-500">{sidebarHint}</p>
            <div className="max-h-[480px] space-y-2 overflow-y-auto">
              {selected.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center gap-2 rounded-lg border border-primary/30 bg-white px-2 py-2"
                >
                  <Thumb src={item.imageUrl} alt={item.name} />
                  <span className="flex-1 truncate text-sm font-medium">{item.name}</span>
                  <button type="button" onClick={() => onToggle(item)} aria-label={`Remove ${item.name}`}>
                    <X size={16} className="text-gray-400" />
                  </button>
                </div>
              ))}
            </div>
          </aside>
        )}
      </div>
    </div>
  )
}

function Thumb({
  src,
  alt,
  large,
}: {
  src?: string | null
  alt: string
  large?: boolean
}) {
  const size = large ? 'h-20 w-20' : 'h-10 w-10'
  if (!src) {
    return <div className={`${size} flex-shrink-0 rounded-full bg-gray-200`} />
  }
  return (
    <div className={`relative ${size} flex-shrink-0 overflow-hidden rounded-full bg-gray-100`}>
      <Image src={src} alt={alt} fill sizes={large ? '80px' : '40px'} className="object-cover" />
    </div>
  )
}
